import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Globe } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SUPPORTED_LANGUAGES } from '@/i18n/languages';

interface LanguageSwitcherProps {
  /** compact = header (globe + code), full = settings row */
  variant?: 'compact' | 'full';
}

export function LanguageSwitcher({ variant = 'compact' }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentLang = i18n.language?.split('-')[0] || 'en';

  const handleChange = (code: string) => {
    i18n.changeLanguage(code);

    // Update ?lng= query param (remove if English to keep URLs clean)
    const next = new URLSearchParams(searchParams);
    if (code === 'en') {
      next.delete('lng');
    } else {
      next.set('lng', code);
    }
    setSearchParams(next, { replace: true });
  };

  if (variant === 'compact') {
    return (
      <Select value={currentLang} onValueChange={handleChange}>
        <SelectTrigger className="h-8 w-auto gap-1.5 border-none bg-transparent px-2 text-sm font-medium shadow-none focus:ring-0 focus:ring-offset-0">
          <Globe className="h-3.5 w-3.5 shrink-0" />
          <SelectValue>{currentLang.toUpperCase()}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {SUPPORTED_LANGUAGES.map((lang) => (
            <SelectItem key={lang.code} value={lang.code}>
              {lang.nativeName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Full variant for settings
  return (
    <div className="space-y-2">
      <Select value={currentLang} onValueChange={handleChange}>
        <SelectTrigger className="w-full max-w-xs">
          <Globe className="h-4 w-4 mr-2 shrink-0" />
          <SelectValue>
            {SUPPORTED_LANGUAGES.find((l) => l.code === currentLang)?.nativeName || 'English'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {SUPPORTED_LANGUAGES.map((lang) => (
            <SelectItem key={lang.code} value={lang.code}>
              <span>{lang.nativeName}</span>
              <span className="ml-2 text-muted-foreground text-xs">({lang.name})</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
